import { EmptyState, LoadingSpinner } from "@oysterhr/ui"
import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { CalculationQueryFields } from "../../types"
import { CalculationsActions } from "../CalculationsActions"
import { CalculationsGrid } from "../CalculationsGrid"
import { useCalculationsParams, useLazyBulkSalaryCalculation } from "../../hooks"
import { useCalculationsProvider } from "../../contexts"
import { matchFeeCurrencyToCalculation } from "../../utils/helpers"

export const CalculationsMainSection = () => {
  // Hooks
  const { getParam, parseUrlToCalculationQueries } = useCalculationsParams()
  const [calculateSalary, { loading }] = useLazyBulkSalaryCalculation()
  const loadedCalculations = useRef(false)

  // Providers
  const { calculations, setCalculations } = useCalculationsProvider()

  const { t } = useTranslation("CostCalculator")

  useEffect(() => {
    const activeIndex = Number(getParam("activeCalculationIndex")) || undefined

    const buildCalculationsFromUrl = async () => {
      const paramCalculationQueries: CalculationQueryFields[] = parseUrlToCalculationQueries()
      // Checks if calculations were already loaded to avoid loading twice and lose salary frequency and type values
      if (paramCalculationQueries.length && !loadedCalculations.current && !calculations.length) {
        const calculationQueries = matchFeeCurrencyToCalculation(paramCalculationQueries)
        loadedCalculations.current = true
        const response = await calculateSalary({
          variables: {
            calculationQueries,
          },
        })

        const calculationsResponse = response.data?.bulkSalaryCalculations
        setCalculations(calculationsResponse || [], activeIndex)
      }
    }

    buildCalculationsFromUrl()
  }, [
    getParam,
    parseUrlToCalculationQueries,
    calculateSalary,
    setCalculations,
    calculations.length,
  ])

  if (calculations.length === 0) {
    // Loading is displayed only when there are no calculations
    // to avoid flickering when adding more calculations
    if (loading) {
      return (
        <div className="mt-8" data-testid="loading">
          <LoadingSpinner />
        </div>
      )
    }
    return (
      <div className="m-auto max-w-md">
        <EmptyState illustrationSrc={EmptyState.Illustration.calculator}>
          <EmptyState.Heading>{t("emptyStateTitle")}</EmptyState.Heading>
          <EmptyState.Content>{t("emptyStateMessage")}</EmptyState.Content>
        </EmptyState>
      </div>
    )
  }
  return (
    <div className="mt-6 w-full space-y-6">
      <CalculationsActions />
      <CalculationsGrid />
    </div>
  )
}
